import {
    ICustomerAttributes,
    StudentAttributes,
    UpdateCustomerType,
    UpdateCustomerPayload,
    ACCOUNT_PAYMENT_TYPE,
    UpdateCustomerV2Type,
} from '@/services/innovorder/customer/customer.types';
import { getAccountPaymentType, getAccountType } from '@/services/innovorder/customer/customer.utils';
import { GuestGroupWithGuestsCount } from '@/services/innovorder/guestGroup/guestGroup.type';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { isString } from 'lodash';

export const isPostPaymentAccountPaymentType = (accountPaymentType: string): boolean => {
    return [ACCOUNT_PAYMENT_TYPE.POSTPAYMENT_BANKDEBIT, ACCOUNT_PAYMENT_TYPE.POSTPAYMENT_GUEST].includes(
        accountPaymentType,
    );
};

export const POST_PAYMENT_EWALLET_OVERDRAFT_DEFAULT_AMOUNT = 1000000;

export const getEWalletOverdraftPayload = (selectedPaymentType: string) => {
    if (isPostPaymentAccountPaymentType(selectedPaymentType)) {
        return {
            paymentDailyLimit: null,
            overrideDailyPaymentLimit: false,
        };
    }

    return {};
};
export const prepareUpdateCustomerPayload = (
    customer: ICustomerAttributes,
    formValues: UpdateCustomerV2Type,
): UpdateCustomerPayload => {
    const { customerId } = customer;

    const {
        accountPaymentType,
        badgeNumber,
        grantCode,
        entranceFeeCode,
        studentNumber,
        tariffCode,
        groupId,
        sectionId,
        subSectionId,
        pricingRuleId,
        isBadgeBlocked,
        ewalletOverdraftAmount,
        overrideEwalletOverdraft,
    } = formValues;

    return {
        ewalletOverdraftAmount,
        overrideEwalletOverdraft,
        isBadgeBlocked,
        badgeNumber,
        customerId,
        ...getEWalletOverdraftPayload(accountPaymentType),
        student: {
            ...getAccountType(accountPaymentType),
            guestGroupId: subSectionId?.value || sectionId?.value || groupId?.value,
            pricingRuleId,
            studentNumber,
            tariffCode,
            grantCode,
            entranceFeeCode,
        },
    };
};
const getAllParentGuestGroups = (guestGroups: GuestGroupWithGuestsCount[], guestGroupId: number | null) => {
    const result: GuestGroupWithGuestsCount[] = [];
    if (!guestGroupId) return result;

    const collectParents = (currentId: number) => {
        const currentGroup = guestGroups.find((group) => group.guestGroupId === currentId);
        if (currentGroup) {
            result.push(currentGroup);
            if (currentGroup.parentGuestGroupId) {
                collectParents(currentGroup.parentGuestGroupId);
            }
        }
    };

    collectParents(guestGroupId);
    return result.sort((a, b) => a.depth - b.depth);
};

const getStudentDefaultValues = (
    student: StudentAttributes,
    guestGroups: GuestGroupWithGuestsCount[],
): Partial<UpdateCustomerType> & {
    groupId?: { value: number; label: string };
    sectionId?: { value: number; label: string };
    subSectionId?: { value: number; label: string };
    pricingRuleId?: number;
} => {
    const {
        tariffCode,
        studentNumber,
        guestGroupId,
        accountPaymentType: groupAccountPaymentType,
        accountType,
        entranceFeeCode,
        grantCode,
        pricingRuleId,
        class: className,
    } = student;

    let resolvedGuestGroupId = guestGroupId;

    if (!resolvedGuestGroupId && className) {
        const matchingGroup = guestGroups?.find((group) => {
            if (!group.name || !className) return false;
            return group.name.toLowerCase() === className.toLowerCase();
        });

        if (matchingGroup) {
            resolvedGuestGroupId = matchingGroup.guestGroupId;
        }
    }

    const populatedGuestGroup = getAllParentGuestGroups(guestGroups, resolvedGuestGroupId);
    const accountPaymentType = getAccountPaymentType(groupAccountPaymentType, accountType);

    return {
        tariffCode: tariffCode || undefined,
        studentNumber: studentNumber || undefined,
        accountPaymentType: accountPaymentType || undefined,
        grantCode: grantCode || undefined,
        entranceFeeCode: entranceFeeCode || undefined,
        groupId: { value: populatedGuestGroup[0]?.guestGroupId, label: populatedGuestGroup[0]?.name },
        sectionId: { value: populatedGuestGroup[1]?.guestGroupId, label: populatedGuestGroup[1]?.name },
        subSectionId: { value: populatedGuestGroup[2]?.guestGroupId, label: populatedGuestGroup[2]?.name },
        pricingRuleId: pricingRuleId || populatedGuestGroup[0]?.pricingRules[0]?.pricingRuleId,
    };
};

export const getEwalletOverdraftValues = ({ paymentDailyLimit, ewalletOverdraftAmount }: ICustomerAttributes) => ({
    paymentDailyLimit,
    overrideDailyPaymentLimit: paymentDailyLimit != null,
    overrideEwalletOverdraft: ewalletOverdraftAmount != null,
    ewalletOverdraftAmount: ewalletOverdraftAmount ?? undefined,
});

export const getCustomerDefaultValues = (
    customer: ICustomerAttributes | undefined,
    guestGroups: GuestGroupWithGuestsCount[],
): Partial<UpdateCustomerV2Type> => {
    if (!customer) {
        return {};
    }

    const { badgeNumber, isBadgeBlocked } = customer;

    if (!customer.student) {
        return {
            isBadgeBlocked,
            badgeNumber: isString(badgeNumber) ? badgeNumber : undefined,
        };
    }

    const studentDefaultValues = getStudentDefaultValues(customer.student, guestGroups);
    const ewalletOverdraftValues = getEwalletOverdraftValues(customer);

    return {
        isBadgeBlocked,
        badgeNumber: isString(badgeNumber) ? badgeNumber : undefined,
        ...ewalletOverdraftValues,
        ...studentDefaultValues,
    };
};

type FormErrorData = {
    code: string;
    message: string;
};

export const getErrorMessage = (errors: FetchBaseQueryError[]): string => {
    return errors.reduce(
        (message, error: FetchBaseQueryError) => message || (error?.data as FormErrorData)?.message,
        '',
    );
};
